import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { INewUserCreate, createUsers } from '../../api/admin';

const MAX_USERS = 10;

interface UserRow {
	key: number;
	full_name: string;
	email: string;
}

interface FieldErrors {
	full_name?: string;
	email?: string;
}

interface Props {
	open: boolean;
	onClose: () => void;
}

let keyCounter = 0;
const newRow = (): UserRow => ({ key: ++keyCounter, full_name: '', email: '' });

function validateEmail(email: string): boolean {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function CreateUsersDialog({ open, onClose }: Props) {
	const [step, setStep] = useState<'fill' | 'confirm'>('fill');
	const [rows, setRows] = useState<UserRow[]>([newRow()]);
	const [fieldErrors, setFieldErrors] = useState<Record<number, FieldErrors>>({});
	const [isVendor, setIsVendor] = useState(false);
	const [isMarketingRecipient, setIsMarketingRecipient] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [succeeded, setSucceeded] = useState(false);

	function resetAndClose() {
		setStep('fill');
		setRows([newRow()]);
		setFieldErrors({});
		setIsVendor(false);
		setIsMarketingRecipient(true);
		setError(null);
		setSucceeded(false);
		onClose();
	}

	function updateRow(key: number, field: keyof Omit<UserRow, 'key'>, value: string) {
		setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
		// clear error on change
		setFieldErrors((prev) => {
			const next = { ...prev };
			if (next[key]) {
				next[key] = { ...next[key], [field]: undefined };
			}
			return next;
		});
	}

	function addRow() {
		if (rows.length < MAX_USERS) setRows((prev) => [...prev, newRow()]);
	}

	function removeRow(key: number) {
		if (rows.length > 1) {
			setRows((prev) => prev.filter((r) => r.key !== key));
			setFieldErrors((prev) => {
				const next = { ...prev };
				delete next[key];
				return next;
			});
		}
	}

	function validate(): boolean {
		const errors: Record<number, FieldErrors> = {};
		for (const row of rows) {
			const rowErrors: FieldErrors = {};
			if (!row.full_name.trim()) rowErrors.full_name = 'Required';
			if (!row.email.trim()) {
				rowErrors.email = 'Required';
			} else if (!validateEmail(row.email)) {
				rowErrors.email = 'Invalid email';
			}
			if (Object.keys(rowErrors).length > 0) errors[row.key] = rowErrors;
		}
		setFieldErrors(errors);
		return Object.keys(errors).length === 0;
	}

	function handleNext() {
		if (validate()) setStep('confirm');
	}

	async function handleConfirm() {
		setSubmitting(true);
		setError(null);
		const payload: INewUserCreate[] = rows.map((r) => ({
			full_name: r.full_name.trim(),
			email: r.email.trim().toLowerCase(),
			is_vendor: isVendor,
			is_marketing_recipient: isMarketingRecipient,
		}));
		try {
			await createUsers(payload);
			setSucceeded(true);
		} catch (e: any) {
			setError(e?.response?.data?.detail ?? 'An unexpected error occurred. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}

	const filledCount = rows.filter((r) => r.full_name.trim() || r.email.trim()).length;

	return (
		<Dialog
			open={open}
			onClose={resetAndClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{succeeded ? (
				<>
					<DialogTitle>Users Created</DialogTitle>
					<DialogContent>
						<Alert severity="success">
							{rows.length} user account{rows.length !== 1 ? 's' : ''} created successfully.
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button variant="contained" onClick={resetAndClose}>
							Done
						</Button>
					</DialogActions>
				</>
			) : step === 'fill' ? (
				<>
					<DialogTitle>Create New Users</DialogTitle>
					<DialogContent sx={{ pb: 1 }}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Enter details for up to {MAX_USERS} users. All fields are required.
						</Typography>					<Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
							<FormControlLabel
								control={
									<Switch
										checked={isMarketingRecipient}
										onChange={(e) => setIsMarketingRecipient(e.target.checked)}
										size="small"
									/>
								}
								label={
									<Typography variant="body2">Marketing Recipient</Typography>
								}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={isVendor}
										onChange={(e) => setIsVendor(e.target.checked)}
										size="small"
									/>
								}
								label={
									<Typography variant="body2">Vendor</Typography>
								}
							/>
						</Box>						<Stack spacing={1.5}>
							{rows.map((row, idx) => (
								<Box key={row.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ width: 20, pt: '10px', textAlign: 'right', flexShrink: 0 }}
									>
										{idx + 1}.
									</Typography>
									<TextField
										label="Full Name"
										size="small"
										value={row.full_name}
										onChange={(e) => updateRow(row.key, 'full_name', e.target.value)}
										error={!!fieldErrors[row.key]?.full_name}
										helperText={fieldErrors[row.key]?.full_name}
										sx={{ flex: 1 }}
									/>
									<TextField
										label="Email"
										type="email"
										size="small"
										value={row.email}
										onChange={(e) => updateRow(row.key, 'email', e.target.value)}
										error={!!fieldErrors[row.key]?.email}
										helperText={fieldErrors[row.key]?.email}
										sx={{ flex: 1.2 }}
									/>
									<Tooltip title="Remove">
										<span>
											<IconButton
												size="small"
												onClick={() => removeRow(row.key)}
												disabled={rows.length === 1}
												sx={{ mt: '4px' }}
											>
												<RemoveCircleOutlineIcon fontSize="small" />
											</IconButton>
										</span>
									</Tooltip>
								</Box>
							))}
						</Stack>
					</DialogContent>
					<DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
						<Button
							startIcon={<AddIcon />}
							onClick={addRow}
							disabled={rows.length >= MAX_USERS}
							size="small"
						>
							Add User
						</Button>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<Button onClick={resetAndClose}>Cancel</Button>
							<Button variant="contained" onClick={handleNext} disabled={filledCount === 0}>
								Review {rows.length} User{rows.length !== 1 ? 's' : ''}
							</Button>
						</Box>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>Confirm New Users</DialogTitle>
					<DialogContent>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<Typography variant="body2" gutterBottom>
							Creating <strong>{rows.length}</strong> new user account
							{rows.length !== 1 ? 's' : ''}:
						</Typography>
						<Divider sx={{ my: 1 }} />
						<List dense disablePadding>
							{rows.map((row) => (
								<ListItem key={row.key} disableGutters sx={{ py: 0.25 }}>
									<ListItemText
										primary={row.full_name.trim()}
										secondary={row.email.trim().toLowerCase()}
										primaryTypographyProps={{ variant: 'body2' }}
										secondaryTypographyProps={{ variant: 'caption' }}
									/>
								</ListItem>
							))}
						</List>
						<Divider sx={{ my: 1 }} />
						<Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
							<Typography variant="body2" color="text.secondary">
								Marketing Recipient: <strong>{isMarketingRecipient ? 'Yes' : 'No'}</strong>
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Vendor: <strong>{isVendor ? 'Yes' : 'No'}</strong>
							</Typography>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								setError(null);
								setStep('fill');
							}}
							disabled={submitting}
						>
							Back
						</Button>
						<Button onClick={resetAndClose} disabled={submitting}>
							Cancel
						</Button>
						<Button
							variant="contained"
							startIcon={
								submitting ? <CircularProgress size={16} /> : <PersonAddIcon />
							}
							onClick={handleConfirm}
							disabled={submitting}
						>
							{submitting ? 'Creating…' : `Create ${rows.length} User${rows.length !== 1 ? 's' : ''}`}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
